/**
 * Header — the in-body banner the legacy SWF drew at the top of every
 * chat frame. Faithful to frame 22 ("chat") of profilechat10-retired.
 *
 * The Radio row's ▶ button toggles live playback of the FFR Icecast
 * stream (URL arrives via `now_playing` events from the server). When
 * the stream is offline we show "[OFFLINE]" and disable the button.
 */
import { rankLabel } from '../lib/ranks.js';
import type { UserView } from '../lib/types.js';
import type { WSState } from '../lib/ws.js';

interface Props {
    me: UserView | null;
    wsState: WSState;
    /** Round-trip ms; null if unknown. */
    pingMs?: number | null;
    /** Open the matching legacy panel. */
    openPanel: (p: 'admin' | 'evil' | 'arnold' | 'random' | 'options') => void;
}

const STATE_COLOR: Record<WSState, string> = {
    connecting: '#F7E138',
    open:       '#13CC23',
    closed:     '#9b77cc',
    error:      '#FF0000',
};

export function Header({ me, wsState, pingMs, openPanel }: Props) {
    const isAdmin = !!me?.is_admin;
    const isMod   = !!me?.is_mod;

    return (
        <>
            <div className="pc-banner" role="banner">
                {/* Logo split into two transparent PNGs (SWF chid 392 + 394)
                    positioned absolutely so the reflection portion of each
                    image can spill downward past the banner edge into the
                    chat body below — matches the legacy SWF look. */}
                <img className="pc-banner-icon"     src="/prochat/app/legacy/ffr-icon.png"     alt="" />
                <img className="pc-banner-wordmark" src="/prochat/app/legacy/ffr-wordmark.png" alt="" />
                <div className="pc-banner-row pc-banner-top">
                    {isAdmin && (
                        <button className="pc-banner-link" type="button" onClick={() => openPanel('admin')}>
                            Admin Panel
                        </button>
                    )}
                </div>
                <div className="pc-banner-row pc-banner-bottom">
                    {isAdmin && (
                        <>
                            <button className="pc-banner-link" type="button" onClick={() => openPanel('evil')}>
                                Evil Admin Panel
                            </button>
                            <span style={{ opacity: .5 }}>|</span>
                            <button className="pc-banner-link" type="button" onClick={() => openPanel('arnold')}>
                                Evil Sound Board
                            </button>
                            <span style={{ opacity: .5 }}>|</span>
                            <button className="pc-banner-link" type="button" onClick={() => openPanel('random')}>
                                Random Sound Board
                            </button>
                        </>
                    )}
                    {/* Right-aligned: 'Options', connection state, current user */}
                    <span className="pc-state">
                        <span className="pc-dot" style={{ background: STATE_COLOR[wsState] }} />
                        {wsState}
                        {pingMs != null && wsState === 'open' && (
                            <span className="pc-ping" title="WebSocket round-trip latency">
                                {pingMs}ms
                            </span>
                        )}
                    </span>
                    <span className="pc-me">
                        {me ? (
                            <>
                                <span className="pc-uname" style={{ color: me.color }}>{me.name}</span>
                                <span className="pc-rank">{rankLabel(me.k)}</span>
                                {isMod && !isAdmin && <span className="pc-rank">(mod)</span>}
                            </>
                        ) : <span className="pc-muted">unauthenticated</span>}
                    </span>
                    <button className="pc-banner-link pc-link-light" type="button" onClick={() => openPanel('options')}>
                        Options
                    </button>
                </div>
            </div>
        </>
    );
}
